import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import React, { useEffect } from "react";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import { getSupportedUserLanguage } from "src/lib/i18next";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import AppViewCubit, {
  OnboardingScreenStatus
} from "src/state/AppViewCubit/AppViewCubit";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import UserCubit, {
  ProfileAttributesKeys
} from "src/state/UserCubit/UserCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { useBloc } from "src/state/state";
import AppointmentsList from "src/ui/components/AppointmentsList/AppointmentsList";
import Branding from "src/ui/components/Branding/Branding";
import DailyReadingsWidget from "src/ui/components/DailyReadings/DailyReadingsWidget";
import Loader from "src/ui/components/Loader/Loader";
import MealInsightUploadCard from "src/ui/components/MealInsights/MealInsightUploadCard";
import ExpectationsManagementScreen from "src/ui/components/ExpectationManagement/ExpectationsManagementScreen";
import OnboardingScreenVisitOnly from "src/ui/components/OnboardingScreenVisitOnly/OnboardingScreenVisitOnly";
import OnboardingSteps from "src/ui/components/OnboardingSteps/OnboardingSteps";
import PoweredByWidget from "src/ui/components/PoweredByWidget/PoweredByWidget";
import RxDietCard from "src/ui/components/RxDiet/RxDietCard";
import AppTopSpacer from "src/ui/components/StyledComponents/AppTopSpacer";
import CollectionTitle from "src/ui/components/StyledComponents/CollectionTitle";
import TaskList from "src/ui/components/TaskList/TaskList";
import Translate from "src/ui/components/Translate/Translate";
import UserEducationalFeed from "src/ui/components/UserEducationalFeed/UserEducationalFeed";
import UserEducationalFeedOld from "src/ui/components/UserEducationalFeed/UserEducationalFeedOld";

const Wrap = styled.div`
  label: HomePageWrap;
  max-width: ${APP_CONTENT_WIDTH}px;
  margin: 0 1.4rem;
  height: 100%;
`;

const Widgets = styled.div`
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const HomePage = () => {
  const [{ showOnboardingScreen }, { checkForOnboardingCompleted }] =
    useBlocNext(AppViewCubit);
  const [, { showManagementExpectation, userHasUnblockingOnboarding }] =
    useBloc(UserPreferencesCubit);
  const [{ profileAttributes }] = useBloc(UserCubit);
  const { headingRef } = useFocusHeading();
  const language = getSupportedUserLanguage();

  const userExperienceGroup =
    profileAttributes?.[ProfileAttributesKeys.experienceGroup];

  const showManagementExpectationScreen =
    showManagementExpectation &&
    userHasUnblockingOnboarding &&
    Boolean(userExperienceGroup);

  useEffect(() => {
    void checkForOnboardingCompleted();
  }, []);

  return (
    <>
      {showOnboardingScreen === OnboardingScreenStatus.completed &&
        !showManagementExpectationScreen && (
          <Wrap>
            <AppTopSpacer pt />
            <h1 className="only-sr" ref={headingRef} {...getHeadingProps()}>
              <Translate msg="homePage" />
            </h1>
            <Widgets>
              <PoweredByWidget />
              <DailyReadingsWidget />
            </Widgets>

            <Branding
              style={{
                marginBlockEnd: "1.6em"
              }}
            />

            <AppointmentsList />

            <TaskList
              programs={[
                KnownProgram.ONBOARDING,
                KnownProgram.CARE,
                KnownProgram.MEAL_PLAN,
                KnownProgram.LIFEBALANCE,
                KnownProgram.BEHAVIORAL_HEALTH_SCREENING,
                KnownProgram.EDUCATION,
                KnownProgram.NUTRITION_JOURNEY,
                KnownProgram.MINDSET_JOURNEY,
                KnownProgram.PHYSICAL_ACTIVITY_JOURNEY,
                KnownProgram.EVERYDAY_DIABETES_JOURNEY
              ]}
              consolidateOnboardingTasks
            />

            <MealInsightUploadCard />

            <RxDietCard />

            <nine-spacer s="md" />
            <CollectionTitle>
              <Translate msg="articles.title" />
            </CollectionTitle>
            {language === "es" ? (
              <UserEducationalFeedOld />
            ) : (
              <UserEducationalFeed />
            )}
          </Wrap>
        )}

      {showOnboardingScreen === OnboardingScreenStatus.initial && (
        <Loader fixed active />
      )}
      {showManagementExpectationScreen && <ExpectationsManagementScreen />}

      {showOnboardingScreen === OnboardingScreenStatus.showFull && (
        <OnboardingSteps />
      )}
      {showOnboardingScreen === OnboardingScreenStatus.showVisitOnly && (
        <OnboardingScreenVisitOnly />
      )}
    </>
  );
};

export default HomePage;
